import type {
  TweetsResponse,
  TweetResponse,
  StatsResponse,
  AuthorsResponse,
  FilterState,
} from '@/types/api';

// Dynamic API_BASE for production (set via vite define)
declare const __API_BASE__: string;
const API_BASE = typeof __API_BASE__ !== 'undefined' ? __API_BASE__ : '/api';

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Helper to handle fetch errors
async function handleResponse(response: Response): Promise<any> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Helper to build query parameters
function buildQueryParams(
  page: number,
  limit: number,
  filters: Partial<FilterState>
): string {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (filters.type && filters.type !== 'all') {
    params.append('type', filters.type);
  }
  if (filters.search) {
    params.append('search', filters.search);
  }
  if (filters.author) {
    params.append('author', filters.author);
  }
  if (filters.hasMedia) {
    params.append('hasMedia', 'true');
  }

  return params.toString();
}

// API client
export const api = {
  // Get tweets with filters and pagination
  async getTweets(
    page: number = 1,
    limit: number = 20,
    filters: Partial<FilterState> = {}
  ): Promise<TweetsResponse> {
    const query = buildQueryParams(page, limit, filters);
    const response = await fetch(`${API_BASE}/tweets?${query}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Get single tweet by ID
  async getTweetById(id: string): Promise<TweetResponse> {
    const response = await fetch(`${API_BASE}/tweets/${id}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Get statistics
  async getStats(): Promise<StatsResponse> {
    const response = await fetch(`${API_BASE}/stats`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Get authors list
  async getAuthors(): Promise<AuthorsResponse> {
    const response = await fetch(`${API_BASE}/authors`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response);
  },

  // Import batch data
  async importBatch(
    type: 'captures' | 'users' | 'tweets',
    batch: any[]
  ): Promise<{ success: boolean; count: number }> {
    const response = await fetch(`${API_BASE}/import/batch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ type, batch }),
    });
    return handleResponse(response);
  },

  // Login
  async login(password: string): Promise<{ token: string }> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    return handleResponse(response);
  },

  // Verify token
  async verifyToken(): Promise<{ valid: boolean; userId?: string }> {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return { valid: false };
    }

    const response = await fetch(`${API_BASE}/auth/verify`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (response.status === 401) {
      localStorage.removeItem('auth_token');
      return { valid: false };
    }

    return handleResponse(response);
  },
};
